import { z } from "@brains/utils/zod";
import type {
  EntityDisplayEntry,
  RouteDefinitionInput,
  SiteContentDefinition,
} from "@rizom/site";
import type { SiteCompositionPlugin } from "./plugin";

/**
 * A site package bundles everything the site-builder needs for
 * site structure:
 * - Page structure (layout components)
 * - Hand-written routes (home, about, etc.)
 * - Data layer (site plugin with templates + datasources)
 * - Display metadata per entity type (labels, navigation, pagination)
 *
 * Themes are resolved separately by the resolver. A brain chooses a
 * site package and a theme independently, even though both choices are
 * colocated under `site:` in brain.yaml.
 *
 * @example
 * ```ts
 * import { personalSitePlugin, PersonalLayout, routes } from "@brains/site-personal";
 *
 * const site: SitePackage = {
 *   layouts: { default: PersonalLayout },
 *   routes,
 *   plugin: personalSitePlugin,
 *   entityDisplay: {
 *     post: { label: "Post" },
 *   },
 * };
 *
 * export default site;
 * ```
 */
export interface SitePackage<
  TPluginConfig = Record<string, unknown>,
  TPlugin extends SiteCompositionPlugin = SiteCompositionPlugin,
> {
  /** Layout components keyed by name — at minimum "default" is required */
  layouts: Record<string, unknown>;

  /** Hand-written route definitions (home, about, etc.) */
  routes: RouteDefinitionInput[];

  /** Optional runtime plugin factory for legacy/internal site integrations. */
  plugin?: ((config?: TPluginConfig) => TPlugin) | undefined;

  /** Optional content definitions owned by this package. */
  content?: SiteContentDefinition | SiteContentDefinition[];

  /**
   * Optional additive CSS owned by the site package.
   *
   * This is layered after the selected base theme and before any
   * instance-level `site.themeOverride`, so package-specific visual
   * fixes travel with the site code without leaking into deploy config.
   */
  themeOverride?: string;

  /** Global head scripts to inject into every rendered page. */
  headScripts?: string[];

  /**
   * Display metadata per entity type — label, plural name, layout,
   * pagination, navigation slot. Consulted by the dynamic route
   * generator when producing auto-generated list/detail routes for
   * active entity plugins.
   */
  entityDisplay: Record<string, EntityDisplayEntry>;

  /**
   * Static assets to write into the site output directory at build time.
   *
   * Keys are output paths relative to the output directory (e.g.
   * `/canvases/tree.js`, `/fonts/foo.woff2`). Values are file contents
   * as strings — typically produced by text imports
   * (`import content from "./foo.js" with { type: "text" }`).
   *
   * Use this to ship static files that belong to the site package
   * itself (canvas scripts, fonts, images encoded as base64, etc.)
   * rather than the consuming app's `public/` directory. The
   * site-builder writes them verbatim next to the rendered HTML.
   */
  staticAssets?: Record<string, string>;
}

export type SitePackageOverrides<
  TPluginConfig = Record<string, unknown>,
  TPlugin extends SiteCompositionPlugin = SiteCompositionPlugin,
> = Partial<SitePackage<TPluginConfig, TPlugin>>;

function normalizeContent(
  content: SiteContentDefinition | SiteContentDefinition[] | undefined,
): SiteContentDefinition[] {
  if (!content) return [];
  return Array.isArray(content) ? content : [content];
}

function mergeContent(
  baseContent: SiteContentDefinition | SiteContentDefinition[] | undefined,
  overrideContent: SiteContentDefinition | SiteContentDefinition[] | undefined,
): SiteContentDefinition[] | undefined {
  const merged = [
    ...normalizeContent(baseContent),
    ...normalizeContent(overrideContent),
  ];
  return merged.length > 0 ? merged : undefined;
}

function mergeRoutes(
  baseRoutes: RouteDefinitionInput[],
  overrideRoutes: RouteDefinitionInput[] | undefined,
): RouteDefinitionInput[] {
  if (!overrideRoutes || overrideRoutes.length === 0) {
    return baseRoutes;
  }

  const mergedRoutes = [...baseRoutes];
  const indexByKey = new Map<string, number>();
  for (const [index, route] of mergedRoutes.entries()) {
    indexByKey.set(route.id, index);
  }

  for (const route of overrideRoutes) {
    const existingIndex = indexByKey.get(route.id);
    if (existingIndex !== undefined) {
      mergedRoutes[existingIndex] = route;
      continue;
    }
    indexByKey.set(route.id, mergedRoutes.length);
    mergedRoutes.push(route);
  }

  return mergedRoutes;
}

export function extendSite<
  TPluginConfig,
  TPlugin extends SiteCompositionPlugin = SiteCompositionPlugin,
>(
  baseSite: SitePackage<TPluginConfig, TPlugin>,
  overrides: SitePackageOverrides<TPluginConfig, TPlugin> = {},
): SitePackage<TPluginConfig, TPlugin> {
  // Fast path: sites/default extends site-professional with {} on every boot,
  // so returning the base unchanged when no overrides are present avoids
  // per-boot object allocation on the resolver hot path.
  if (Object.keys(overrides).length === 0) {
    return baseSite;
  }

  const {
    layouts: overrideLayouts,
    entityDisplay: overrideEntityDisplay,
    staticAssets: overrideStaticAssets,
    themeOverride: overrideThemeOverride,
    headScripts: overrideHeadScripts,
  } = overrides;
  const plugin = Object.hasOwn(overrides, "plugin")
    ? overrides.plugin
    : baseSite.plugin;

  const layouts = overrideLayouts
    ? { ...baseSite.layouts, ...overrideLayouts }
    : baseSite.layouts;

  const entityDisplay = overrideEntityDisplay
    ? { ...baseSite.entityDisplay, ...overrideEntityDisplay }
    : baseSite.entityDisplay;

  const staticAssets = overrideStaticAssets
    ? { ...(baseSite.staticAssets ?? {}), ...overrideStaticAssets }
    : baseSite.staticAssets;
  const themeOverride = [baseSite.themeOverride, overrideThemeOverride]
    .filter(Boolean)
    .join("\n\n");
  const headScripts = [
    ...(baseSite.headScripts ?? []),
    ...(overrideHeadScripts ?? []),
  ];
  const content = mergeContent(baseSite.content, overrides.content);

  return {
    layouts,
    routes: mergeRoutes(baseSite.routes, overrides.routes),
    ...(plugin ? { plugin } : {}),
    entityDisplay,
    ...(content ? { content } : {}),
    ...(themeOverride ? { themeOverride } : {}),
    ...(headScripts.length > 0 ? { headScripts } : {}),
    ...(staticAssets && Object.keys(staticAssets).length > 0
      ? { staticAssets }
      : {}),
  };
}

export const themeCssSchema: z.ZodString = z.string();

// Runtime gate for site packages loaded dynamically from a package ref at
// boot. The full structural type is enforced statically by `SitePackage`
// for in-tree consumers; this only catches dynamic-import shapes.
const sitePackageRouteShapeSchema: z.ZodType<{ id: string }> = z.looseObject({
  id: z.string().min(1),
});

const entityDisplayEntrySchema: z.ZodType<{ label: string }> = z.looseObject({
  label: z.string().min(1),
});

const sitePackageShapeSchema: z.ZodType<unknown> = z.looseObject({
  layouts: z.record(z.string(), z.unknown()),
  plugin: z
    .custom<SitePackage["plugin"]>((value) => typeof value === "function")
    .optional(),
  routes: z.array(sitePackageRouteShapeSchema),
  entityDisplay: z.record(z.string(), entityDisplayEntrySchema),
  content: z.unknown().optional(),
  themeOverride: z.string().optional(),
  headScripts: z.array(z.string()).optional(),
  staticAssets: z.record(z.string(), z.string()).optional(),
});

export const sitePackageSchema: z.ZodType<SitePackage> = z.custom<SitePackage>(
  (value) => sitePackageShapeSchema.safeParse(value).success,
);
